/**
 * R138d · Feb 2026 — Entity Role Defs manager.
 * ------------------------------------------------------------
 * Modal that lets workspace admins customise the Client / Brand
 * membership-role catalog: rename existing roles, edit which
 * permission codes each role grants, add new custom roles, and
 * delete non-system roles that aren't in use.
 *
 * System-locked roles (owner, viewer) can be edited (label,
 * description, permissions) but not deleted, and their role_key
 * is immutable.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Trash2, Plus, Save, Lock } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

// The universe of permission codes a client/brand role can grant.
// Kept in sync with backend CLIENT_BRAND_ROLE_PERMS union.
const CLIENT_BRAND_PERM_CODES = [
  "view", "edit", "delete", "member_manage",
  "task.create", "task.view", "rfq.create", "rfq.view",
  "finance.view", "comment",
];

export default function EntityRoleDefsDialog({ open, onOpenChange, workspaceId, onSaved }) {
  const [kindTab, setKindTab] = useState("client");
  const [defsByKind, setDefsByKind] = useState({ client: [], brand: [], project: [] });
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState({});     // {def_id: {label, description, permissions}}
  const [creating, setCreating] = useState(false);
  const [newRole, setNewRole] = useState({ role_key: "", label: "", description: "", permissions: [] });
  // R138f · Feb 2026 — Workspace permission catalog for the Project
  // tab's chip picker. Client/brand keep the fixed 10-code list.
  const [projectPermGroups, setProjectPermGroups] = useState([]);
  const [projectPermAll, setProjectPermAll] = useState([]);
  const [projectPermQuery, setProjectPermQuery] = useState("");

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setBusy(true);
    try {
      // R138e / R138f · Feb 2026 — Load client + brand + project defs
      // all from the customisable `entity_role_defs` collection in a
      // single call. Also fetch the workspace permission catalog so
      // the Project tab can render its (much larger) permission chip
      // picker.
      const [defsRes, permsRes] = await Promise.all([
        api.get(`/workspaces/${workspaceId}/entity-role-defs`),
        api.get(`/permissions`).catch(() => ({ data: { groups: [], all: [] } })),
      ]);
      setDefsByKind({
        client: defsRes.data.client || [],
        brand: defsRes.data.brand || [],
        project: defsRes.data.project || [],
      });
      setProjectPermGroups(permsRes.data?.groups || []);
      setProjectPermAll(permsRes.data?.all || []);
      setDrafts({});
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load role definitions"));
    } finally { setBusy(false); }
  }, [workspaceId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const currentDefs = defsByKind[kindTab] || [];

  const getDraft = (def) => drafts[def.id] || {
    label: def.label,
    description: def.description || "",
    permissions: def.permissions || [],
  };

  const setDraft = (defId, patch) => {
    setDrafts((prev) => ({ ...prev, [defId]: { ...(prev[defId] || {}), ...patch } }));
  };

  const isDirty = (def) => {
    const d = drafts[def.id];
    if (!d) return false;
    if (d.label !== def.label) return true;
    if ((d.description || "") !== (def.description || "")) return true;
    const a = new Set(d.permissions || []);
    const b = new Set(def.permissions || []);
    if (a.size !== b.size) return true;
    for (const x of a) if (!b.has(x)) return true;
    return false;
  };

  const saveRow = async (def) => {
    const d = drafts[def.id];
    if (!d) return;
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/workspaces/${workspaceId}/entity-role-defs/${def.id}`,
        { label: d.label, description: d.description, permissions: d.permissions });
      toast.success(`Saved ${data.label || def.role_key}`);
      setDrafts((prev) => { const n = { ...prev }; delete n[def.id]; return n; });
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setBusy(false); }
  };

  const deleteRow = async (def) => {
    if (!window.confirm(`Delete role "${def.label}" for ${kindTab}s? Memberships using this role must be reassigned first.`)) return;
    setBusy(true);
    try {
      await api.delete(`/workspaces/${workspaceId}/entity-role-defs/${def.id}`);
      toast.success(`Deleted ${def.label}`);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Delete failed"));
    } finally { setBusy(false); }
  };

  const createRow = async () => {
    if (!newRole.role_key.trim() || !newRole.label.trim()) {
      toast.error("Role key and label are required");
      return;
    }
    setBusy(true);
    try {
      await api.post(
        `/workspaces/${workspaceId}/entity-role-defs/${kindTab}`,
        newRole);
      toast.success(`Created ${newRole.label}`);
      setCreating(false);
      setNewRole({ role_key: "", label: "", description: "", permissions: [] });
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Create failed"));
    } finally { setBusy(false); }
  };

  const togglePerm = (defId, isNew, code) => {
    if (isNew) {
      setNewRole((prev) => ({
        ...prev,
        permissions: prev.permissions.includes(code)
          ? prev.permissions.filter((c) => c !== code)
          : [...prev.permissions, code],
      }));
      return;
    }
    const draft = getDraft(currentDefs.find((d) => d.id === defId));
    const nextPerms = draft.permissions.includes(code)
      ? draft.permissions.filter((c) => c !== code)
      : [...draft.permissions, code];
    setDraft(defId, { permissions: nextPerms });
  };

  const dirtyCount = useMemo(
    () => currentDefs.filter((d) => isDirty(d)).length,
    [currentDefs, drafts],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="entity-role-defs-dialog">
        <DialogHeader>
          <DialogTitle>Access role catalog</DialogTitle>
          <DialogDescription className="text-xs">
            Customise what each membership role grants on clients and brands.
            System roles (Owner, Viewer) can be renamed and re-scoped but not
            deleted. New custom roles can be added and reused across the workspace.
          </DialogDescription>
        </DialogHeader>

        <div className="inline-flex border border-border p-0.5 bg-muted/40" data-testid="entity-role-defs-kind-tabs">
          {["client", "brand", "project"].map((k) => (
            <button key={k} type="button"
                    onClick={() => setKindTab(k)}
                    className={`px-3 py-1 text-[11px] uppercase tracking-widest font-semibold ${kindTab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid={`entity-role-defs-kind-${k}`}>
              {k}
            </button>
          ))}
          {dirtyCount > 0 && (
            <Badge variant="outline" className="ml-2 border-yellow-500 text-yellow-800" data-testid="entity-role-defs-dirty-badge">
              {dirtyCount} unsaved
            </Badge>
          )}
        </div>

        {/* R138e / R138f · Feb 2026 — Project roles are now fully
            customisable per workspace via `entity_role_defs` too.
            Show a helper note about global fallback + link to the
            workspace roles page for admins who want to change the
            global catalog. */}
        {kindTab === "project" && (
          <div className="p-2 border border-border bg-muted/20 text-[11px]"
               data-testid="entity-role-defs-project-banner">
            Project roles are customisable per workspace. Overrides here take precedence over the global
            {" "}
            <a href="/app/roles" className="underline font-semibold text-primary">
              Workspace Roles
            </a>
            {" "}
            catalog when resolving project permissions. System keys (owner/admin/manager/designer/viewer/external) can be re-scoped and relabelled but not deleted.
          </div>
        )}

        <div className="space-y-3">
          {busy && currentDefs.length === 0 && (
            <div className="text-xs text-muted-foreground">Loading…</div>
          )}
          {currentDefs.map((def) => {
            const d = getDraft(def);
            const dirty = isDirty(def);
            // R138f · Feb 2026 — Project roles are now editable, so
            // readOnly is only true when the backend explicitly flags
            // the row as `read_only_here` (legacy shape).
            const readOnly = !!def.read_only_here;
            const usage = Number(def.usage_count ?? 0);
            return (
              <div key={def.id || def.role_key}
                   className={`border ${dirty ? "border-yellow-500" : "border-border"} p-3 space-y-2`}
                   data-testid={`entity-role-def-row-${def.role_key}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Key</span>
                  <code className="text-[11px] bg-muted/50 px-1.5 py-0.5 border border-border" data-testid={`entity-role-def-key-${def.role_key}`}>
                    {def.role_key}
                  </code>
                  {def.is_system && (
                    <Badge variant="outline" className="text-[9px] border-emerald-400 text-emerald-800" data-testid={`entity-role-def-system-${def.role_key}`}>
                      <Lock className="w-2.5 h-2.5 mr-0.5" /> System
                    </Badge>
                  )}
                  {/* R138e · Feb 2026 — Usage counter. Nudges admins
                      to know how many active memberships will be
                      impacted before they re-scope this role. */}
                  <Badge variant="outline"
                          className={`text-[9px] ${usage > 0 ? "border-primary/60 text-primary" : "border-border text-muted-foreground"}`}
                          data-testid={`entity-role-def-usage-${def.role_key}`}>
                    · {usage} membership{usage === 1 ? "" : "s"}
                  </Badge>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground ml-2">Label</span>
                  <Input value={d.label}
                         onChange={(e) => setDraft(def.id, { label: e.target.value })}
                         disabled={readOnly}
                         className="h-7 text-xs w-52 rounded-none"
                         data-testid={`entity-role-def-label-${def.role_key}`} />
                  <div className="ml-auto flex items-center gap-1">
                    {dirty && !readOnly && (
                      <Button size="sm" onClick={() => saveRow(def)} disabled={busy}
                              className="h-7 text-[10px]"
                              data-testid={`entity-role-def-save-${def.role_key}`}>
                        <Save className="w-3 h-3 mr-1" /> Save
                      </Button>
                    )}
                    {def.is_deletable && !def.is_system && !readOnly && (
                      <Button size="sm" variant="outline" onClick={() => deleteRow(def)} disabled={busy}
                              className="h-7 text-[10px] text-red-700 border-red-300 hover:bg-red-50"
                              data-testid={`entity-role-def-delete-${def.role_key}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
                <Input value={d.description}
                       onChange={(e) => setDraft(def.id, { description: e.target.value })}
                       disabled={readOnly}
                       placeholder="Short description shown in the Team Matrix help popover"
                       className="h-7 text-xs rounded-none"
                       data-testid={`entity-role-def-desc-${def.role_key}`} />
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Grants</span>
                  {readOnly ? (
                    (def.permissions || []).length === 0
                      ? <span className="text-[10px] text-muted-foreground italic">Managed in Workspace Roles</span>
                      : (def.permissions || []).map((code) => (
                          <span key={code} className="text-[10px] px-1.5 py-0.5 border border-border bg-muted/30 text-muted-foreground">
                            {code}
                          </span>
                        ))
                  ) : kindTab === "project" ? (
                    /* R138f · Feb 2026 — Project roles pull from the
                       workspace permission catalog (~200 codes). Chip
                       list would overwhelm — use a compact grouped
                       picker with a header search box + collapsible
                       groups so admins can toggle in bulk. */
                    <ProjectPermsPicker
                      selected={d.permissions || []}
                      onToggle={(code) => togglePerm(def.id, false, code)}
                      groups={projectPermGroups}
                      allCodes={projectPermAll}
                      query={projectPermQuery}
                      setQuery={setProjectPermQuery}
                      testidPrefix={`entity-role-def-perm-${def.role_key}`}
                    />
                  ) : (
                    CLIENT_BRAND_PERM_CODES.map((code) => {
                      const on = (d.permissions || []).includes(code);
                      return (
                        <button key={code} type="button"
                                onClick={() => togglePerm(def.id, false, code)}
                                className={`text-[10px] px-1.5 py-0.5 border ${on ? "bg-primary/10 border-primary text-primary font-semibold" : "border-border text-muted-foreground hover:bg-muted"}`}
                                data-testid={`entity-role-def-perm-${def.role_key}-${code}`}>
                          {code}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}

          {creating ? (
            <div className="border border-primary p-3 space-y-2" data-testid="entity-role-def-new-form">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Key</span>
                <Input value={newRole.role_key}
                       onChange={(e) => setNewRole({ ...newRole, role_key: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
                       placeholder="e.g. approver"
                       className="h-7 text-xs w-40 rounded-none"
                       data-testid="entity-role-def-new-key" />
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Label</span>
                <Input value={newRole.label}
                       onChange={(e) => setNewRole({ ...newRole, label: e.target.value })}
                       placeholder="e.g. Approver"
                       className="h-7 text-xs w-52 rounded-none"
                       data-testid="entity-role-def-new-label" />
                <div className="ml-auto flex items-center gap-1">
                  <Button size="sm" onClick={createRow} disabled={busy}
                          className="h-7 text-[10px]" data-testid="entity-role-def-new-save">
                    <Save className="w-3 h-3 mr-1" /> Create
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setCreating(false)}
                          className="h-7 text-[10px]" data-testid="entity-role-def-new-cancel">
                    Cancel
                  </Button>
                </div>
              </div>
              <Input value={newRole.description}
                     onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                     placeholder="Description"
                     className="h-7 text-xs rounded-none"
                     data-testid="entity-role-def-new-desc" />
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Grants</span>
                {kindTab === "project" ? (
                  <ProjectPermsPicker
                    selected={newRole.permissions}
                    onToggle={(code) => togglePerm(null, true, code)}
                    groups={projectPermGroups}
                    allCodes={projectPermAll}
                    query={projectPermQuery}
                    setQuery={setProjectPermQuery}
                    testidPrefix="entity-role-def-new-perm"
                  />
                ) : (
                  CLIENT_BRAND_PERM_CODES.map((code) => {
                    const on = newRole.permissions.includes(code);
                    return (
                      <button key={code} type="button"
                              onClick={() => togglePerm(null, true, code)}
                              className={`text-[10px] px-1.5 py-0.5 border ${on ? "bg-primary/10 border-primary text-primary font-semibold" : "border-border text-muted-foreground hover:bg-muted"}`}
                              data-testid={`entity-role-def-new-perm-${code}`}>
                        {code}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setCreating(true)} disabled={busy}
                    data-testid="entity-role-def-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Add custom role
            </Button>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="entity-role-defs-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



/**
 * R138f · Feb 2026 — Compact permission picker for the Project role
 * tab. Renders a search box + collapsible groups sourced from the
 * workspace permission catalog (GET /permissions). Selected count is
 * shown up top so admins can eyeball the size of the grant.
 */
function ProjectPermsPicker({ selected, onToggle, groups, allCodes, query, setQuery, testidPrefix }) {
  const [openGroups, setOpenGroups] = useState({});
  const selectedSet = new Set(selected || []);
  const term = (query || "").toLowerCase().trim();
  const filteredGroups = (groups || []).map((g) => ({
    ...g,
    permissions: (g.permissions || []).filter((p) =>
      !term
      || p.code.toLowerCase().includes(term)
      || (p.label || "").toLowerCase().includes(term)),
  })).filter((g) => g.permissions.length > 0);
  const totalMatched = filteredGroups.reduce((n, g) => n + g.permissions.length, 0);
  return (
    <div className="w-full space-y-1.5" data-testid={testidPrefix}>
      <div className="flex items-center gap-2 flex-wrap">
        <input value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search permissions (${allCodes?.length || 0} available)…`}
                className="h-7 text-[11px] w-64 px-2 border border-border rounded-none bg-background"
                data-testid={`${testidPrefix}-search`} />
        <span className="text-[10px] text-muted-foreground">
          {selectedSet.size} selected · {totalMatched} match{totalMatched === 1 ? "" : "es"}
        </span>
        {selected?.length > 0 && (
          <button type="button"
                  className="text-[10px] text-muted-foreground underline"
                  onClick={() => (selected || []).forEach((c) => onToggle(c))}
                  data-testid={`${testidPrefix}-clear-all`}>
            Clear all
          </button>
        )}
      </div>
      <div className="max-h-64 overflow-y-auto border border-border p-2 space-y-1.5 bg-background">
        {filteredGroups.length === 0 && (
          <div className="text-[10px] text-muted-foreground italic">No matching permissions.</div>
        )}
        {filteredGroups.map((g) => {
          const groupIsOpen = openGroups[g.label] ?? (!!term || false);
          const groupSelected = g.permissions.filter((p) => selectedSet.has(p.code)).length;
          return (
            <div key={g.label} className="border border-border">
              <button type="button"
                      onClick={() => setOpenGroups({ ...openGroups, [g.label]: !groupIsOpen })}
                      className="w-full flex items-center gap-2 px-2 py-1 bg-muted/40 hover:bg-muted text-[10px] uppercase tracking-widest font-semibold"
                      data-testid={`${testidPrefix}-group-${g.label}`}>
                <span>{groupIsOpen ? "▼" : "▶"}</span>
                <span>{g.label}</span>
                <span className="ml-auto text-muted-foreground">
                  {groupSelected}/{g.permissions.length}
                </span>
              </button>
              {groupIsOpen && (
                <div className="p-1.5 flex flex-wrap gap-1">
                  {g.permissions.map((p) => {
                    const on = selectedSet.has(p.code);
                    return (
                      <button key={p.code} type="button"
                              onClick={() => onToggle(p.code)}
                              className={`text-[10px] px-1.5 py-0.5 border ${on ? "bg-primary/10 border-primary text-primary font-semibold" : "border-border text-muted-foreground hover:bg-muted"}`}
                              title={p.label || p.code}
                              data-testid={`${testidPrefix}-${p.code}`}>
                        {p.code}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
